// utils/timezone.js
//
// HAB light schedules are stored on the device as UTC "HHMM" integers, but the
// user picks on/off times in the grid's LOCAL time. These helpers convert
// between the two, honoring DST for the relevant date — built on the platform
// `Intl` API so we add no dependency (consistent with toEasternPlotDate).
//
// Note on the integer format: utc_on/utc_off are HHMM as an int, so leading
// zeros drop — 200 means 02:00 UTC (2:00 AM), not 2 minutes. Always pad to 4
// digits before splitting hours/minutes.

// Some grid records carry non-canonical timezone names (e.g. "US/New_York",
// which isn't a valid IANA zone) — Intl throws a RangeError on those. Normalize
// known legacy/loose aliases to canonical IANA names, and fall back to UTC for
// anything we can't validate so a bad value never crashes the page.
const TZ_ALIASES = {
  'US/Eastern': 'America/New_York',
  'US/New_York': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Alaska': 'America/Anchorage',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Arizona': 'America/Phoenix',
};

const tzValidity = {};
const isValidTz = (tz) => {
  if (tz in tzValidity) return tzValidity[tz];
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    tzValidity[tz] = true;
  } catch {
    tzValidity[tz] = false;
  }
  return tzValidity[tz];
};

// A guaranteed Intl-safe IANA zone: the input if valid, else a known alias,
// else UTC.
export const resolveTimeZone = (tz) => {
  if (!tz) return 'UTC';
  if (isValidTz(tz)) return tz;
  const alias = TZ_ALIASES[tz];
  if (alias && isValidTz(alias)) return alias;
  return 'UTC';
};

// Minutes to ADD to a UTC instant to get local wall-clock time in `timeZone`
// (e.g. America/New_York in summer → -240). Derived by formatting the instant
// in the zone and diffing against the same fields read as UTC.
const tzOffsetMinutes = (timeZone, date) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone), hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = {};
  for (const { type, value } of dtf.formatToParts(date)) {
    if (type !== 'literal') p[type] = value;
  }
  const asUTC = Date.UTC(
    +p.year, +p.month - 1, +p.day,
    p.hour === '24' ? 0 : +p.hour, +p.minute, +p.second
  );
  return (asUTC - date.getTime()) / 60000;
};

// The local calendar date (Y/M/D) in `timeZone` for a given instant.
const localDateParts = (timeZone, date) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone), year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const p = {};
  for (const { type, value } of dtf.formatToParts(date)) {
    if (type !== 'literal') p[type] = value;
  }
  return { y: +p.year, mo: +p.month, d: +p.day };
};

// "HHMM" int → { h, m }. Pads leading zeros: 200 → { h: 2, m: 0 }.
export const hhmmToParts = (hhmm) => {
  const s = String(hhmm ?? 0).padStart(4, '0');
  return { h: +s.slice(0, 2), m: +s.slice(2) };
};

// { h, m } → "HHMM" int (as a number, so 02:00 → 200).
export const partsToHhmm = (h, m) => h * 100 + m;

// "HH:MM" string (e.g. from <input type="time">) → "HHMM" int, and back.
export const timeStringToHhmm = (str) => {
  if (!str || !/^\d{1,2}:\d{2}$/.test(str)) return null;
  const [h, m] = str.split(':').map(Number);
  return partsToHhmm(h, m);
};
export const hhmmToTimeString = (hhmm) => {
  const { h, m } = hhmmToParts(hhmm);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Local wall-clock (h, m) in `timeZone` → UTC "HHMM" int. Uses `refDate`'s local
// day in the zone to pick the right DST offset (defaults to today).
export const localToUtcHhmm = (h, m, timeZone, refDate = new Date()) => {
  const { y, mo, d } = localDateParts(timeZone, refDate);
  // Treat the local wall time as if it were UTC, then correct by the zone's
  // offset at that instant. One correction is exact except within the ~1hr DST
  // transition window, which is immaterial for a daily on/off schedule.
  let utcMs = Date.UTC(y, mo - 1, d, h, m);
  const off = tzOffsetMinutes(timeZone, new Date(utcMs));
  utcMs -= off * 60000;
  const out = new Date(utcMs);
  return partsToHhmm(out.getUTCHours(), out.getUTCMinutes());
};

// UTC "HHMM" int → local wall-clock { h, m } in `timeZone` (for display/pickers).
export const utcHhmmToLocal = (hhmm, timeZone, refDate = new Date()) => {
  const { h: uh, m: um } = hhmmToParts(hhmm);
  const utcMs = Date.UTC(
    refDate.getUTCFullYear(), refDate.getUTCMonth(), refDate.getUTCDate(), uh, um
  );
  const off = tzOffsetMinutes(timeZone, new Date(utcMs));
  // Shift by the offset so the UTC fields now read the local wall clock.
  const local = new Date(utcMs + off * 60000);
  return { h: local.getUTCHours(), m: local.getUTCMinutes() };
};

// UTC "HHMM" int → a friendly local 12-hour label, e.g. 2300 → "7:00 PM".
export const formatUtcHhmmLocal = (hhmm, timeZone) => {
  const { h, m } = utcHhmmToLocal(hhmm, timeZone);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};
